/**
 * ============================================================
 * MAIN PRODUCT VARIANT FIX + HARD MEDIA SET FILTER (NO FLASH)
 * ------------------------------------------------------------
 * Keeps your cart-variant fix exactly, and adds:
 * - Filters gallery to ONLY the selected Color's image set
 * - Uses metafield variant_media_map (VariantMediaMap script tag)
 * - Uses data-media-filename from Liquid (stable, no CDN guessing)
 * - Re-applies after Refresh gallery re-renders (MutationObserver)
 * ============================================================
 */

(function () {
  "use strict";

  document.addEventListener("DOMContentLoaded", () => {
    // 1) Locate ProductJson + bind to correct product-info
    const productJsonEl =
      document.querySelector(
        'product-info[id^="MainProduct-"] script[type="application/json"][id^="ProductJson-"]'
      ) || document.querySelector('script[type="application/json"][id^="ProductJson-"]');

    if (!productJsonEl) {
      console.warn("[Variant Fix] ProductJson not found.");
      return;
    }

    const root = productJsonEl.closest("product-info") || document;

    let product;
    try {
      product = JSON.parse(productJsonEl.textContent);
    } catch (e) {
      console.warn("[Variant Fix] Failed to parse ProductJson.", e);
      return;
    }

    // 2) Main product form only
    const sectionId = root.getAttribute("data-section") || root.dataset?.section || "";

    const form =
      (sectionId && root.querySelector(`form#product-form-${CSS.escape(sectionId)}`)) ||
      root.querySelector('product-form form[action^="/cart/add"]') ||
      root.querySelector('form[action^="/cart/add"][id^="product-form-"]') ||
      root.querySelector('form[action^="/cart/add"]');

    if (!form) {
      console.warn("[Variant Fix] Main add-to-cart form not found.");
      return;
    }

    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) {
      console.warn("[Variant Fix] input[name=id] not found.");
      return;
    }

    const mainFormId = form.getAttribute("id") || "";

    // ------------------------------------------------------------
    // Helpers: option selection
    // ------------------------------------------------------------
    function optionIndexFromInput(input) {
      const pos = input.getAttribute?.("data-option-position");
      if (pos) return parseInt(pos, 10) - 1;

      const di = input.getAttribute?.("data-index");
      if (di === "option1") return 0;
      if (di === "option2") return 1;
      if (di === "option3") return 2;

      const name = input.getAttribute?.("name") || "";
      const m = name.match(/^options\[(.+)\]$/);
      if (m && m[1] && Array.isArray(product.options)) {
        return product.options.indexOf(m[1]);
      }
      return -1;
    }

    function getOptionControls() {
      const insideForm = Array.from(form.querySelectorAll('input[type="radio"], select'));

      const linkedToForm = mainFormId
        ? Array.from(
            root.querySelectorAll(
              `input[type="radio"][form="${CSS.escape(mainFormId)}"], select[form="${CSS.escape(
                mainFormId
              )}"]`
            )
          )
        : [];

      return Array.from(new Set([...insideForm, ...linkedToForm]));
    }

    function getSelectedOptions() {
      const optionCount = Array.isArray(product.options) ? product.options.length : 0;
      if (!optionCount) return null;

      const selected = new Array(optionCount).fill(null);
      const controls = getOptionControls();

      controls
        .filter((el) => el.matches && el.matches('input[type="radio"]') && el.checked)
        .forEach((input) => {
          const idx = optionIndexFromInput(input);
          if (idx >= 0) selected[idx] = input.value;
        });

      controls
        .filter((el) => el.matches && el.matches("select"))
        .forEach((select) => {
          const idx = optionIndexFromInput(select);
          if (idx >= 0) selected[idx] = select.value;
        });

      if (selected.some((v) => !v)) return null;
      return selected;
    }

    function findVariant(selectedOptions) {
      return (product.variants || []).find((v) => {
        if (!Array.isArray(v.options)) return false;
        return v.options.every((opt, i) => opt === selectedOptions[i]);
      });
    }

    function setUrlVariant(variantId) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", String(variantId));
        window.history.replaceState({}, "", url.toString());
      } catch (_) {}
    }

    // ------------------------------------------------------------
    // MEDIA FILTER (variant_media_map)
    // ------------------------------------------------------------
    function ensureMediaFilterCSS() {
      if (document.getElementById("media-filter-css")) return;
      const style = document.createElement("style");
      style.id = "media-filter-css";
      style.textContent = `
        .is-variant-hidden{display:none!important;}
        .is-variant-fade{opacity:0;transition:opacity .22s ease;}
        .is-variant-show{opacity:1;transition:opacity .22s ease;}
      `;
      document.head.appendChild(style);
    }

    function readVariantMediaMap() {
      const el = root.querySelector("#VariantMediaMap") || document.querySelector("#VariantMediaMap");
      if (!el) return null;
      try {
        return JSON.parse(el.textContent || "{}");
      } catch (e) {
        console.warn("[Media Filter] VariantMediaMap JSON invalid.", e);
        return null;
      }
    }

    function normalizeFilename(name) {
      if (!name) return "";
      const lower = String(name).toLowerCase();
      const noQuery = lower.split("?")[0];
      const file = noQuery.split("/").pop() || "";

      const dot = file.lastIndexOf(".");
      const base = dot > -1 ? file.slice(0, dot) : file;

      // strip Shopify size suffix like _800x800, _1024x, _x800
      return base.replace(/_\d+x\d+$/i, "").replace(/_\d+x$/i, "").replace(/_x\d+$/i, "");
    }

    function getSelectedColorValue() {
      const colorIdx = Array.isArray(product.options) ? product.options.indexOf("Color") : -1;
      const selected = getSelectedOptions();
      if (selected && colorIdx >= 0) return selected[colorIdx];
      return "";
    }

    function getMediaNodes() {
      // Grab common media nodes; we'll hide the node itself, not just images
      const nodes = [
        ...root.querySelectorAll(".product__media-item"),
        ...root.querySelectorAll(".product__media-list > li"),
        ...root.querySelectorAll("media-gallery [data-media-id]"),
      ];
      return Array.from(new Set(nodes)).filter(Boolean);
    }

    let filterTimer = null;
    function scheduleApplyMediaFilter() {
      window.clearTimeout(filterTimer);
      filterTimer = window.setTimeout(applyMediaFilter, 30);
    }

    function applyMediaFilter() {
      const map = readVariantMediaMap();
      if (!map) return;

      const color = (getSelectedColorValue() || "").trim();
      if (!color) return;

      const list = map[color] || map[String(color).toUpperCase()] || map[String(color).toLowerCase()];
      if (!Array.isArray(list) || list.length === 0) return;

      ensureMediaFilterCSS();

      const allowedBases = list.map(normalizeFilename);
      const nodes = getMediaNodes();
      if (!nodes.length) return;

      // Fade everything, then show only allowed
      nodes.forEach((n) => {
        n.classList.add("is-variant-fade");
        n.classList.remove("is-variant-show");
      });

      requestAnimationFrame(() => {
        nodes.forEach((node) => {
          // Prefer Liquid-provided filename attribute (stable)
          const dataName = node.getAttribute("data-media-filename") || "";
          let base = normalizeFilename(dataName);

          // Fallback: try img src if attribute missing
          if (!base) {
            const img = node.querySelector("img");
            const src = img?.currentSrc || img?.src || "";
            base = normalizeFilename(src);
          }

          // If we still can't identify it (videos/models), keep visible
          if (!base) {
            node.classList.remove("is-variant-hidden");
            node.classList.remove("is-variant-fade");
            node.classList.add("is-variant-show");
            return;
          }

          const shouldShow = allowedBases.includes(base);
          node.classList.toggle("is-variant-hidden", !shouldShow);

          node.classList.remove("is-variant-fade");
          node.classList.add("is-variant-show");
        });
      });
    }

    // MutationObserver: Refresh often re-renders the gallery after variant changes
    // This makes sure hidden images don't pop back in.
    const galleryRoot =
      root.querySelector("media-gallery") ||
      root.querySelector(".product__media") ||
      root.querySelector(".product__media-wrapper") ||
      root;

    const observer = new MutationObserver(() => {
      scheduleApplyMediaFilter();
    });

    observer.observe(galleryRoot, { childList: true, subtree: true });

    // ------------------------------------------------------------
    // Main sync (NO gallery selection; only variant ID + URL + events)
    // ------------------------------------------------------------
    function syncVariant() {
      const selected = getSelectedOptions();
      if (!selected) return;

      const variant = findVariant(selected);
      if (!variant) return;

      idInput.value = variant.id;
      idInput.dispatchEvent(new Event("change", { bubbles: true }));

      setUrlVariant(variant.id);

      try {
        const evt = new CustomEvent("variant:change", {
          bubbles: true,
          detail: { variant },
        });
        form.dispatchEvent(evt);
      } catch (_) {}

      // Apply media filter after sync
      applyMediaFilter();
    }

    root.addEventListener("change", (e) => {
      const t = e.target;
      if (!t || !(t.matches && t.matches('input[type="radio"], select'))) return;

      const isInsideForm = form.contains(t);
      const isLinked = mainFormId && t.getAttribute("form") === mainFormId;

      if (isInsideForm || isLinked) syncVariant();
    });

    form.addEventListener("variant:change", () => applyMediaFilter());
    form.addEventListener("submit", () => syncVariant(), true);

    // Initial sync
    syncVariant();
  });
})();
